import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, X, Link2 } from "lucide-react";
import { getCases, getMeta, getHeroCase, getFeaturedGrid, getPatterns, ACCENTS } from "@/lib/apiClient";
import { FeaturedCard, CaseCard } from "@/components/CaseCards";
import { SkeletonGrid } from "@/components/Skeletons";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const BIZ_FILTERS = [
  { label: "All", value: "" },
  { label: "Traditional Trades", value: "Traditional Trade" },
  { label: "Traditional Business", value: "Traditional Business" },
  { label: "Professional Services", value: "Professional Services" },
  { label: "Modern / Digital", value: "Modern / Digital Business" },
  { label: "AI Native", value: "AI-Native Business" },
];

const BLANK_FILTERS = { business_type: "", industry: "", country: "", region: "", transformation_type: "", q: "" };

const CHIP_LABEL = {
  business_type: "Type", industry: "Industry", country: "Country",
  region: "Region", transformation_type: "Transformation", q: "Search",
};

function HeroFeature({ c }) {
  if (!c) return null;
  const a = ACCENTS[c.accent] || ACCENTS.yellow;
  return (
    <Link to={`/case/${c.slug}`} data-testid="hero-featured-card" className="group lift-card block border-2 border-ink overflow-hidden hover:shadow-[8px_8px_0_0_#0A0A0C]">
      <div className="zoom-frame aspect-[16/10] border-b-2 border-ink">
        <img src={c.image} alt={c.company} className="w-full h-full object-cover" />
      </div>
      <div className={`${a.bg} ${a.text} p-6`}>
        <span className={`eyebrow ${a.badge} px-2 py-1`}>{c.industry} · {c.country}</span>
        <h3 className="font-display font-extrabold text-2xl sm:text-3xl leading-[1.03] tracking-tight mt-4">{c.headline}</h3>
        <div className="mt-5 flex items-end justify-between gap-4">
          <div>
            <div className="font-display font-extrabold text-4xl tracking-tighter leading-none">{c.key_metric}</div>
            <div className={`text-xs mt-1 ${a.sub}`}>{c.metric_context}</div>
          </div>
          <span className="inline-flex items-center gap-1 font-bold text-sm">Read case <ArrowRight size={16} className="arrow-move" /></span>
        </div>
      </div>
    </Link>
  );
}

const selectCls = "h-11 px-3 pr-8 border-2 border-ink bg-white font-semibold text-sm outline-none focus:shadow-[3px_3px_0_0_#0A0A0C] transition-shadow cursor-pointer";

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [featuredGrid, setFeaturedGrid] = useState([]);
  const [hero, setHero] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [cases, setCases] = useState([]);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(() => {
    const f = { ...BLANK_FILTERS };
    for (const k of Object.keys(BLANK_FILTERS)) { const v = searchParams.get(k); if (v) f[k] = v; }
    return f;
  });
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    Promise.all([getMeta(), getHeroCase(), getFeaturedGrid(4)]).then(([m, h, f]) => {
      setMeta(m);
      setHero(h);
      setFeaturedGrid(f);
    });
    getPatterns().then(setPatterns).catch(() => {});
  }, []);

  // debounce search into filters.q
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: search.trim() })), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setFading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    setSearchParams(params, { replace: true });
    getCases(params).then((data) => {
      setCases(data);
      setLoading(false);
      setTimeout(() => setFading(false), 30);
    });
  }, [filters]);

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Link copied — share these filters");
  };

  const heroCase = hero || featuredGrid[0];

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearF = (k) => { if (k === "q") setSearch(""); setFilters((f) => ({ ...f, [k]: "" })); };
  const clearAll = () => { setSearch(""); setFilters(BLANK_FILTERS); };
  const activeChips = Object.entries(filters).filter(([, v]) => v);

  const ticker = useMemo(() => {
    const stats = [
      `${meta?.total_published ?? "20+"} VERIFIED CASES`,
      `${meta?.industries?.length ?? "14"} INDUSTRIES`,
      `${meta?.countries?.length ?? "8"} COUNTRIES`,
      "EVIDENCE FIRST",
      "REAL SOURCES ONLY",
    ];
    return [...stats, ...stats];
  }, [meta]);

  return (
    <div>
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="enter" style={{ animationDelay: "40ms" }}>
            <p className="eyebrow text-ink/60">REAL BUSINESSES. <span className="text-brorange">REAL AI TRANSFORMATIONS.</span></p>
            <h1 data-testid="hero-title" className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl display-tight uppercase mt-4">
              AI is changing<br />business. <span className="text-cobalt">Fast.</span>
            </h1>
            <p className="text-base sm:text-lg text-ink/70 mt-6 max-w-xl leading-relaxed">
              See how real companies around the world are using AI to transform how they work —
              and explore what might be possible in your market.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#latest" data-testid="explore-cases-button" className="group btn-tactile inline-flex items-center gap-2 bg-ink text-white font-bold px-6 h-12 hover:bg-cobalt">
                Explore Cases <ArrowRight size={18} className="arrow-move" />
              </a>
              <Link to="/trends" data-testid="view-trends-button" className="group btn-tactile inline-flex items-center gap-2 bg-white text-ink font-bold px-6 h-12 border-2 border-ink hover:bg-bryellow">
                View Trends <ArrowRight size={18} className="arrow-move" />
              </Link>
            </div>
          </div>
          <div className="enter" style={{ animationDelay: "160ms" }}>
            <HeroFeature c={heroCase} />
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="border-y-2 border-ink bg-bryellow overflow-hidden">
        <div className="ticker-track py-2.5">
          {ticker.map((t, i) => (
            <span key={i} className="eyebrow text-ink px-6 flex items-center gap-6">{t} <span className="text-brorange">✦</span></span>
          ))}
        </div>
      </div>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight uppercase">Featured Transformations</h2>
          <a href="#latest" className="group hidden sm:inline-flex items-center gap-1 font-semibold text-sm shrink-0">See more cases <ArrowRight size={15} className="arrow-move" /></a>
        </div>
        {featuredGrid.length === 0 ? (
          <SkeletonGrid count={4} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredGrid.map((c, i) => (
              <Reveal key={c.slug} delay={i * 90}><FeaturedCard c={c} index={i} /></Reveal>
            ))}
          </div>
        )}
      </section>

      {/* PATTERNS STRIP */}
      {patterns.length > 0 && (
        <section className="border-y-2 border-ink bg-ink text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <p className="eyebrow text-bryellow">Evidence, not predictions</p>
                <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight uppercase mt-2">Patterns we're seeing</h2>
              </div>
              <Link to="/patterns" data-testid="home-all-patterns" className="group hidden sm:inline-flex items-center gap-1 font-semibold text-sm shrink-0 text-bryellow">All patterns <ArrowRight size={15} className="arrow-move" /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5" data-testid="home-patterns-strip">
              {patterns.slice(0, 3).map((p, i) => (
                <Reveal key={p.slug} delay={i * 80}>
                  <Link to={`/pattern/${p.slug}`} data-testid={`home-pattern-${p.slug}`} className="group lift-card block border-2 border-white/30 p-5 h-full hover:border-bryellow hover:bg-white/5 transition-colors">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="eyebrow bg-bryellow text-ink px-2 py-0.5">{p.supporting_count} cases</span>
                      <span className="eyebrow border border-white/40 px-2 py-0.5">{(p.industries || []).length} industries</span>
                      <span className="eyebrow border border-white/40 px-2 py-0.5">{(p.countries || []).length} countries</span>
                    </div>
                    <h3 className="font-display font-extrabold text-xl leading-[1.1] tracking-tight group-hover:text-bryellow transition-colors">{p.name}</h3>
                    <p className="text-sm text-white/70 mt-2 leading-snug line-clamp-3">{p.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1 font-bold text-sm text-bryellow">Explore Pattern <ArrowRight size={15} className="arrow-move" /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LATEST */}
      <section id="latest" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 scroll-mt-24">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight uppercase mb-6">Latest Transformations</h2>

        {/* business-type pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {BIZ_FILTERS.map((f) => (
            <button
              key={f.label}
              data-testid={`filter-pill-${f.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
              onClick={() => setF("business_type", f.value)}
              className={`btn-tactile px-4 h-10 border-2 border-ink font-semibold text-sm transition-colors ${filters.business_type === f.value ? "bg-ink text-white" : "bg-white text-ink hover:bg-bryellow"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* dropdowns + search */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select data-testid="filter-industry" className={selectCls} value={filters.industry} onChange={(e) => setF("industry", e.target.value)}>
            <option value="">All Industries</option>
            {(meta?.industries || []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select data-testid="filter-country" className={selectCls} value={filters.country} onChange={(e) => setF("country", e.target.value)}>
            <option value="">All Countries</option>
            {(meta?.countries || []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select data-testid="filter-region" className={selectCls} value={filters.region} onChange={(e) => setF("region", e.target.value)}>
            <option value="">All Regions</option>
            {(meta?.regions || []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select data-testid="filter-transformation" className={selectCls} value={filters.transformation_type} onChange={(e) => setF("transformation_type", e.target.value)}>
            <option value="">All Transformations</option>
            {(meta?.transformation_types || []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <input
            data-testid="filter-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search companies, tech, keywords…"
            className="h-11 px-3 border-2 border-ink bg-white font-semibold text-sm outline-none focus:shadow-[3px_3px_0_0_#0A0A0C] transition-shadow min-w-[220px] flex-1"
          />
        </div>

        {/* active-filter chips */}
        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="active-filter-chips">
            {activeChips.map(([k, v]) => (
              <button key={k} onClick={() => clearF(k)} data-testid={`chip-${k}`} className="inline-flex items-center gap-1.5 bg-ink text-white eyebrow px-3 h-8 hover:bg-brorange transition-colors">
                {CHIP_LABEL[k]}: {k === "business_type" ? (BIZ_FILTERS.find((b) => b.value === v)?.label || v) : v} <X size={13} />
              </button>
            ))}
            <button onClick={clearAll} data-testid="clear-all-filters" className="eyebrow px-3 h-8 border-2 border-ink hover:bg-bryellow transition-colors">Clear all</button>
            <button onClick={copyLink} data-testid="copy-filter-link" className="inline-flex items-center gap-1.5 eyebrow px-3 h-8 border-2 border-ink hover:bg-cobalt hover:text-white transition-colors"><Link2 size={13} /> Share filters</button>
          </div>
        )}
        {activeChips.length === 0 && <div className="mb-8" />}

        {loading ? (
          <SkeletonGrid count={8} />
        ) : cases.length === 0 ? (
          <div data-testid="empty-state" className="border-2 border-ink p-12 text-center">
            <h3 className="font-display font-extrabold text-2xl">Nothing here yet.</h3>
            <p className="text-ink/60 mt-2 max-w-md mx-auto">We haven't found a verified AI transformation matching those filters.</p>
            <button onClick={clearAll} data-testid="clear-filters-button" className="btn-tactile mt-5 inline-flex items-center gap-2 bg-ink text-white font-bold px-5 h-11 hover:bg-cobalt">Clear Filters</button>
          </div>
        ) : (
          <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-5 transition-opacity duration-200 ${fading ? "opacity-0" : "opacity-100"}`}>
            {cases.map((c, i) => (
              <Reveal key={c.slug} delay={Math.min(i, 8) * 60}><CaseCard c={c} /></Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
